<template>
  <button
    type="button"
    :class="
      $b({
        active,
        preorder,
        disabled,
        isStatic,
        wide,
        grey,
        soldout,
      })
    "
    :disabled="isStatic || disabled || soldout"
    @click="$emit('click')"
  >
    <div :class="$b('inner')">
      <div :class="$b('badge-and-icon')">
        <VisionIcon
          v-if="option.icon"
          transition
          :icon="option.icon"
          :size="option.iconSize || 'medium'"
          :class="$b('icon')"
          colour="black"
        />

        <BaseCopy
          v-if="option.title"
          tag="span"
          size="h5"
          :class="$b('label')"
          text-transform="uppercase"
        >
          {{ option.title }}
        </BaseCopy>

        <BaseCopy
          v-if="option.label"
          tag="span"
          size="h5"
          :class="$b('label')"
        >
          {{ option.label }}
        </BaseCopy>
        <template v-if="soldout">
          <BaseCopy
            small
            tag="span"
            :size="wide ? 'h5' : 'body-sm'"
            :class="$b('price')"
          >
            {{ $tr('Sold Out') }}
          </BaseCopy>
        </template>
        <template v-else-if="preorder">
          <BaseCopy
            small
            tag="span"
            :size="wide ? 'h5' : 'body-sm'"
            :class="$b('price')"
          >
            {{ $tr('Pre-order') }}
          </BaseCopy>
        </template>

        <template v-else>
          <BaseCopy
            v-if="!isStatic && option.price"
            small
            tag="span"
            :size="wide ? 'h5' : 'body-sm'"
            :class="$b('price')"
          >
            {{ wide && !option.icon ? '' : $tcs(option.price) }}
          </BaseCopy>
          <BaseCopy
            v-else-if="!isStatic && showPriceDifference"
            small
            tag="span"
            :size="wide ? 'h5' : 'body-sm'"
            :class="$b('price')"
          >
            <span v-if="priceDifference === 0">&ndash;</span>
            <span v-else>
              {{ pricePrepend
              }}{{ wide && !option.icon ? '' : $tcs(priceDifference) }}
            </span>
          </BaseCopy>
        </template>
      </div>

      <template v-if="wide">
        <BaseCopy
          tag="span"
          size="body-sm"
          :class="$b('description')"
        >
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="option.description" />
        </BaseCopy>

        <BaseCopy
          v-if="!isStatic && option.price && !soldout"
          size="body-sm"
          tag="span"
          :class="$b('price', { inline: true })"
        >
          {{ $tcs(option.price) }}
        </BaseCopy>
        <BaseCopy
          v-if="soldout"
          size="body-sm"
          tag="span"
          :class="$b('price', { inline: true })"
        >
          {{ $tr('Sold Out') }}
        </BaseCopy>
      </template>
    </div>
  </button>
</template>

<script>
export default {
  name: 'TechOption',

  props: {
    option: { type: Object, required: true },
    isStatic: { type: Boolean, default: false },
    wide: { type: Boolean, default: false },
    grey: { type: Boolean, default: false },
    lensTechSelected: { type: Boolean, required: false },
  },

  emits: ['click'],

  computed: {
    active() {
      return this.option.active
    },
    disabled() {
      return this.option.disabled
    },
    preorder() {
      return this.option.preorder
    },
    soldout() {
      return !this.option.inStock && !this.option.preorder
    },
    hasPriceDifference() {
      return Number.isFinite(this.option.priceDifference)
    },
    priceDifference() {
      return Math.abs(this.option.priceDifference)
    },
    pricePrepend() {
      if (this.option.priceDifference < 0) return '-'
      return '+'
    },
    showPriceDifference() {
      return this.hasPriceDifference && this.lensTechSelected
    },
  },
}
</script>

<style lang="postcss">
.TechOption {
  @apply w-full
    flex flex-col items-center justify-center
    text-center rounded-md
    hover:border-off-black relative overflow-hidden
    border border-grey
    transition-all duration-100;

  &--soldout {
    @apply opacity-60;
  }

  &--is-static {
    @apply border-0 text-grey;
  }

  &--active {
    @apply border border-off-black;
  }
}

.TechOption__inner {
  @apply px-xxs py-sm
    flex items-center;
}

.TechOption__badge-and-icon {
  @apply flex flex-col space-y-xxxs
    h-full w-full
    justify-between items-center;

  .TechOption--wide & {
    @apply flex-shrink-0
      mr-xxs;
    width: 60px;

    @screen sm {
      width: 90px;
    }
  }
}

.TechOption__icon {
  @apply text-sm md:text-base
    transform scale-[2] h-sm;
  ;
  --icon-fill: theme('colors.off-black') !important;
  transition: fill 25s ease-in-out;

  .TechOption--soldout &,
  .TechOption--preorder & {
    --icon-fill: theme('colors.grey.600') !important;
  }
}

.TechOption__price {
  @apply leading-none text-off-black;
  margin-top: 5px;
  transition: color 0.25s ease-in-out;
  -webkit-tap-highlight-color: transparent;
  font-size: 12px;

  .TechOption--soldout &,
  .TechOption--preorder & {
    @apply text-grey-600;
  }
}

.TechOption__label {
  @apply text-off-black;
  transition: colour 0.25s ease-in-out;
  -webkit-tap-highlight-color: transparent;
}

.TechOption--wide {
  @apply bg-grey-light border-grey-light;
  min-height: 80px;
  transition: none;

  &.TechOption--active {
    @apply border-off-black;
  }

  .TechOption__price {
    @apply sm:hidden font-normal text-off-black;
    -webkit-tap-highlight-color: transparent;

    .TechOption--soldout &,
    .TechOption--preorder & {
      @apply text-grey-600;
    }

    &--inline {
      @apply font-bold text-right
        visible block smMax:hidden
        ml-auto
        flex-shrink-0;

      width: 80px;
    }
  }
}

.TechOption__description {
  @apply text-left
    sm:mr-xs;

  .TechOption--soldout &,
  .TechOption--preorder & {
    @apply text-grey-600;
  }
}
</style>
